import { useEffect, useState } from "react";

import { MasterPane } from "../AdminListRow";
import { adminHref, type AuditFilter } from "../adminRoute";
import type { AuditApi } from "./auditApi";
import { AuditEventDetail } from "./AuditEventDetail";
import { AuditFilters } from "./AuditFilters";
import { AuditList } from "./AuditList";
import { useAuditT } from "./useAuditT";

/**
 * Audit section. Wide: filters and list (master) beside the routed event.
 * Narrow: the list at /admin/audit, the event at /admin/audit/{id}; filters
 * stay in the query so back restores the same list.
 */
export function AuditPage({
  api,
  wide,
  eventId,
  filter,
  onNavigate,
}: {
  api: AuditApi;
  wide: boolean;
  eventId: string | null;
  filter: AuditFilter;
  onNavigate: (href: string) => void;
}) {
  const t = useAuditT();
  const [eventTypes, setEventTypes] = useState<string[]>([]);
  useEffect(() => {
    let active = true;
    api
      .eventTypes()
      .then((types) => {
        if (active) setEventTypes(types);
      })
      .catch(() => {
        // The type filter degrades to "any"; the list reports real failures.
      });
    return () => {
      active = false;
    };
  }, [api]);

  const listHref = adminHref({ section: "audit", auditFilter: filter });
  const openEvent = (id: string) => {
    onNavigate(
      adminHref({ section: "audit", auditEventId: id, auditFilter: filter }),
    );
  };
  const list = (
    <MasterPane label={t("admin.audit.title")}>
      <AuditFilters
        filter={filter}
        eventTypes={eventTypes}
        wide={wide}
        onChange={(next) => {
          onNavigate(adminHref({ section: "audit", auditFilter: next }));
        }}
      />
      <AuditList
        key={JSON.stringify(filter)}
        api={api}
        filter={filter}
        selectedId={eventId}
        hrefFor={(id) =>
          adminHref({ section: "audit", auditEventId: id, auditFilter: filter })
        }
        onOpen={openEvent}
      />
    </MasterPane>
  );
  const detail =
    eventId === null ? null : (
      <AuditEventDetail
        key={eventId}
        api={api}
        eventId={eventId}
        onNavigate={onNavigate}
        onBack={
          wide
            ? undefined
            : () => {
                onNavigate(listHref);
              }
        }
      />
    );
  return (
    <section className="flex flex-col gap-3">
      {wide && <h2 className="text-title text-fg">{t("admin.audit.title")}</h2>}
      {wide ? (
        <div className="grid min-w-0 grid-cols-[minmax(260px,340px)_minmax(0,1fr)] items-start gap-5">
          {list}
          {detail ?? (
            <p className="rounded-xl bg-sunken p-6 text-body text-fg-muted">
              {t("admin.audit.pick")}
            </p>
          )}
        </div>
      ) : (
        (detail ?? list)
      )}
    </section>
  );
}
